import React, { type JSX } from "react";
import { ExclamationTriangleIcon, ArrowDownTrayIcon } from "@heroicons/react/24/outline";

import { type ErrorData } from "./documentPreviewTypes";

/** What the user can do about the SharePoint failures we recognise. */
const ERROR_GUIDANCE: Record<string, JSX.Element> = {
  sharepoint_denied: (
    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-left">
      <p className="text-xs text-yellow-800 font-medium mb-2">What you can do:</p>
      <ul className="text-xs text-yellow-700 space-y-1 list-disc list-inside">
        <li>Contact your SharePoint administrator to restore access</li>
        <li>Open the file directly in SharePoint if you still have web access</li>
        <li>Request the document owner to reshare the file with you</li>
      </ul>
    </div>
  ),
  missing_identifiers: (
    <div className="mb-4 p-3 bg-cvc-lb2 border border-cvc-lb1 rounded-md text-left">
      <p className="text-xs text-cvc-blue font-medium mb-2">What you can do:</p>
      <ul className="text-xs text-cvc-blue space-y-1 list-disc list-inside">
        <li>Refresh the project context from the sidebar</li>
        <li>If the issue persists, contact support</li>
      </ul>
    </div>
  ),
};

export interface PreviewErrorProps {
  error: ErrorData;
  /** Present when content was downloaded before the failure, so it can be saved. */
  objectUrl: string | null;
  onRetry: () => void;
  onDownload: () => void;
}

export default function PreviewError({
  error,
  objectUrl,
  onRetry,
  onDownload,
}: PreviewErrorProps): JSX.Element {
  return (
    <div className="flex items-center justify-center h-full p-8">
      <div className="text-center max-w-lg">
        <ExclamationTriangleIcon className="h-12 w-12 mx-auto mb-4 text-red-400" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Preview Error</h3>
        <p className="text-sm text-gray-600 mb-4">{error.message}</p>

        {ERROR_GUIDANCE[error.type]}

        <div className="flex gap-2 justify-center">
          <button
            onClick={onRetry}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-cvc-blue border border-transparent rounded-md hover:bg-cvc-marine focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cvc-blue"
          >
            Try Again
          </button>
          {objectUrl && (
            <button
              onClick={onDownload}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-cvc-text bg-white border border-cvc-grey rounded-md hover:bg-cvc-lb2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cvc-blue"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Download PDF
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
